import { SCOPES_CONHECIDOS, Scope, zod } from '@acme/core'
import { z } from 'zod'

import ApiService from '../api.service'

const scopeSchema = z.enum(
    SCOPES_CONHECIDOS as unknown as [string, ...string[]],
)

export const createApiClientSchema = zod.ApiClientSchema.omit({
    __typename: true,
    id: true,
    clienteId: true,
    clientId: true,
    secretHash: true,
    ultimoAcessoEm: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    ativo: true,
}).extend({
    nome: z.string().trim().min(1, 'Nome é obrigatório'),
    scopes: z.array(scopeSchema).min(1, 'Selecione pelo menos um escopo'),
    rateLimitRpm: z.coerce
        .number()
        .int('Deve ser um número inteiro')
        .min(1, 'Mínimo de 1 requisição por minuto')
        .default(60),
    ativo: z
        .union([
            z.enum(['S', 'N']),
            z.boolean().transform(v => (v ? 'S' : 'N')),
        ])
        .optional(),
})

export const updateApiClientSchema = createApiClientSchema.extend({
    id: z.number(),
    ativo: z
        .union([
            z.enum(['S', 'N']),
            z.boolean().transform(v => (v ? 'S' : 'N')),
        ])
        .optional(),
})

export type CreateApiClientInput = z.infer<typeof createApiClientSchema>
export type UpdateApiClientInput = z.infer<typeof updateApiClientSchema>

export interface ApiClient {
    id: number
    nome: string
    clientId: string
    scopes: Scope[]
    rateLimitRpm: number
    ativo: 'S' | 'N'
    ultimoAcessoEm: string | null
    createdAt: string
    updatedAt: string
}

export interface ApiClientWithSecret extends ApiClient {
    clientSecret: string
}

const service = new ApiService<
    CreateApiClientInput | UpdateApiClientInput,
    ApiClient
>('api-clients')

export const apiClients = Object.assign(service, {
    regenerarSegredo: (id: number) =>
        service.postFrom<Record<string, never>, ApiClientWithSecret>(
            id,
            'regenerar-segredo',
            {},
        ),
})

/**
 * Visão administrativa das credenciais de um cliente.
 *
 * Rota própria (`/api-clients/cliente/:clienteId`) e subject próprio
 * (`CredenciaisDeAPIDoCliente`) na API. Administrador e Suporte chegam aqui
 * pela tela por linha em Administração — o cliente usa `apiClients`, que só
 * enxerga o próprio tenant.
 */
const serviceCliente = new ApiService<UpdateApiClientInput, ApiClient>(
    'api-clients/cliente',
)

export const apiClientsCliente = Object.assign(serviceCliente, {
    /** PATCH /v1/api-clients/cliente/:clienteId/:id */
    atualizar: (
        clienteId: number,
        id: number,
        data: Partial<UpdateApiClientInput>,
    ) =>
        serviceCliente.patchFrom<Partial<UpdateApiClientInput>, ApiClient>(
            clienteId,
            String(id),
            data,
        ),
})
